<script setup lang="ts">
import ChevronRightIcon from '@/components/icons/ChevronRightIcon.vue';
import FileCheckIcon from '@/components/icons/FileCheckIcon.vue';
import FileSymlinkIcon from '@/components/icons/FileSymlinkIcon.vue';
import PenToolIcon from '@/components/icons/PenToolIcon.vue';
import RefreshCcwDotIcon from '@/components/icons/RefreshCcwDotIcon.vue';
import { computed } from 'vue';

interface FeaturedLink {
	featured_link_title: string;
	featured_link_subtitle?: string;
	featured_link_link?: {
		url: string;
		title: string;
		target: string;
	};
	featured_link_is_blue: boolean;
	featured_link_use_custom_image: boolean;
	featured_link_icon?: string;
	featured_link_image?: {
		url: string;
		alt: string;
	};
}

interface Props {
	title: string;
	featuredLinks: FeaturedLink[];
}

const props = defineProps<Props>();

const filteredFeaturedLinks = computed(() => {
	return props.featuredLinks.filter(link => link.featured_link_link?.url);
});

const getIconComponent = (iconName: string) => {
	switch (iconName) {
		case 'file-check':
			return FileCheckIcon;
		case 'file-symlink':
			return FileSymlinkIcon;
		case 'pen-tool':
			return PenToolIcon;
		case 'refresh-ccw-dot':
			return RefreshCcwDotIcon;
		default:
			return null;
	}
};
</script>

<template>
	<div class="layout-featured-links">
		<h3 v-if="title" class="layout-featured-links__title" v-html="title"></h3>
		<div class="layout-featured-links__grid">
			<a
				v-for="(link, index) in filteredFeaturedLinks"
				:key="index"
				:href="link.featured_link_link.url"
				:target="link.featured_link_link.target || '_self'"
				class="featured-link"
				:class="{
					'featured-link--blue': link.featured_link_is_blue,
					'featured-link--white': !link.featured_link_is_blue
				}"
			>
				<div
					v-if="link.featured_link_use_custom_image && link.featured_link_image"
					class="featured-link__image-wrapper"
				>
					<img
						:src="link.featured_link_image.url"
						:alt="link.featured_link_image.alt"
						class="featured-link__image"
					>
				</div>
				<div class="featured-link__icon"
					v-if="!link.featured_link_use_custom_image && link.featured_link_icon"
				>
					<div

						class="featured-link__svg-circle"
						:class="{
							'featured-link__svg-circle--blue': link.featured_link_is_blue,
							'featured-link__svg-circle--white': !link.featured_link_is_blue
						}"
					>
						<component
							:is="getIconComponent(link.featured_link_icon)"
							class="featured-link__svg"
						/>
					</div>
				</div>
				<div class="featured-link__content">
					<div class="featured-link__title" v-html="link.featured_link_title"></div>
					<div v-if="link.featured_link_subtitle" class="featured-link__subtitle" v-html="link.featured_link_subtitle"></div>
				</div>
				<div class="featured-link__arrow">
					<ChevronRightIcon />
				</div>
			</a>
		</div>
	</div>
</template>

<style scoped lang="scss">
.layout-featured-links {
	display: grid;
	gap: 20px;
	padding-block: 20px;
}

.layout-featured-links__title {
	font-size: 1.125rem;
	font-weight: 600;
	margin: 0;
	color: $grey-darker;
}

.layout-featured-links__grid {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.featured-link {
	display: flex;
	align-items: center;
	gap: 15px;
	padding: 10px;
	border-radius: 3px;
	text-decoration: none;
	transition: all 0.2s ease;
	color: $brand-blue-dark;

	&:hover {
		.featured-link__title {
			text-decoration: underline;
		}
	}

	&--white {
		background-color: $white;
		border: 1px solid $border-color;
	}

	&--blue {
		background-color: $brand-blue-background;
		border: 1px solid $brand-blue-background;
	}
}

.featured-link__icon {
	flex-shrink: 0;
	width: 60px;
	height: 60px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: $brand-primary;
	margin-left: 5px;
}

.featured-link__image-wrapper {
	width: auto;
	min-width: 157px;
	height: 118px;
	aspect-ratio: 4/3;
	border-radius: 4px;
	overflow: hidden;
	display: flex;
	align-items: center;
	justify-content: center;
}

.featured-link__image {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.featured-link__svg-circle {
	width: 60px;
	height: 60px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;

	&--white {
		background-color: $brand-blue-background;
	}

	&--blue {
		background-color: $white;
	}
}

.featured-link__svg {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
}

.featured-link__content {
	flex: 1;
	padding: 5px 0;
}

.featured-link__title {
	font-size: 1.125rem;
	font-weight: 700;
	margin: 0;
}

.featured-link__subtitle {
	font-size: 0.875rem;
}

.featured-link__arrow {
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	color: $brand-primary;
}
</style>
